import type { ReviewRowModel } from '@seek/libs-shared';

import type { AboutModel } from '../../pages/about/aboutModel';
import type { ReviewsTabModel } from '../../pages/reviews/reviewsTabModel';
import type { SalariesTabModel } from '../../pages/salaries/salariesTabModel';

import type { SeoStructuredData } from './SeoStructuredData';
import {
  EMPLOYER_AGGREGATE_RATING_BEST,
  EMPLOYER_AGGREGATE_RATING_WORST,
} from './constants';

interface BaseJsonLd {
  '@context': string;
  '@type': string;
}

interface AggregateRatingJsonLd {
  '@type': 'AggregateRating';
  ratingValue: number;
  bestRating?: number;
  ratingCount?: number;
  ratingExplanation?: string;
  reviewCount?: number;
  worstRating?: number;
}

interface OrganizationJsonLd {
  '@type': 'Organization';
  name: string;
  '@id'?: string;
  logo?: string;
  url?: string;
}

interface LocalBusinessJsonLd extends BaseJsonLd {
  '@type': 'LocalBusiness';
  name: string;
  address?: string;
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingCount: number;
    ratingValue: number;
  };
  logo?: string;
  review?: ReviewJsonLd[];
  url?: string;
}

interface EmployerAggregateRatingJsonLd extends BaseJsonLd {
  '@type': 'EmployerAggregateRating';
  itemReviewed: OrganizationJsonLd;
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  ratingExplanation?: string;
  worstRating?: number;
}

interface WebPageJsonLd extends BaseJsonLd {
  '@id': string;
  '@type': 'WebPage';
  inLanguage: string;
  name: string;
  url: string;
}

type JsonLd =
  | LocalBusinessJsonLd
  | EmployerAggregateRatingJsonLd
  | WebPageJsonLd
  | (BaseJsonLd & AggregateRatingJsonLd);

interface ReviewJsonLd {
  '@type': string;
  author?: {
    '@type': string;
    jobTitle?: string;
  };
  datePublished?: string;
  reviewBody?: string;
  reviewRating?: {
    '@type': string;
    ratingValue?: number;
  };
}

export const mapToReviewJsonLd = (review: ReviewRowModel) => {
  const formattedDate = review.createdAt ? review.createdAt.split('T')[0] : '';
  return {
    '@type': 'Review',
    author: { '@type': 'Person', jobTitle: review.jobTitle },
    reviewBody: review.title,
    datePublished: formattedDate,
    reviewRating: { '@type': 'Rating', ratingValue: review.overallRating },
  };
};

export const mapToJsonLd = (data: SeoStructuredData): Array<JsonLd> => {
  const items: Array<JsonLd> = [];

  if (data.url) {
    const webPage: WebPageJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': data.url,
      url: data.url,
      name: data.name,
      inLanguage: data.language || 'en',
    };
    items.push(webPage);
  }

  const employerAggregateOrAggregate:
    | EmployerAggregateRatingJsonLd
    | (BaseJsonLd & AggregateRatingJsonLd) =
    data.ratingValue !== null && data.ratingCount !== null
      ? {
          '@context': 'https://schema.org',
          '@type': 'EmployerAggregateRating',
          itemReviewed: {
            '@type': 'Organization',
            name: data.name,
            ...(data?.url ? { sameAs: data?.url } : {}),
            ...(data.logo ? { logo: data.logo } : {}),
          },
          ratingValue: data.ratingValue,
          reviewCount: data.ratingCount,
          bestRating: data.bestRating ?? EMPLOYER_AGGREGATE_RATING_BEST,
          worstRating: data.worstRating ?? EMPLOYER_AGGREGATE_RATING_WORST,
          ...(data.ratingExplanation
            ? { ratingExplanation: data.ratingExplanation }
            : {}),
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'AggregateRating',
          ratingValue: data.ratingValue,
          reviewCount: data.ratingCount,
          bestRating: data.bestRating,
          worstRating: data.worstRating,
          ...(data.ratingExplanation
            ? { ratingExplanation: data.ratingExplanation }
            : {}),
        };

  items.push(employerAggregateOrAggregate);

  // Only include LocalBusiness for non-salaries tabs
  if (!data.isSalariesTab) {
    const localBusiness: LocalBusinessJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: data.name,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingCount: data.ratingCount,
        ratingValue: data.ratingValue,
      },
      ...(data.location ? { address: data.location } : {}),
      ...(data.url ? { url: data.url } : {}),
      ...(data.logo ? { logo: data.logo } : {}),
      ...(data.reviews?.length
        ? { review: data.reviews.map((r) => mapToReviewJsonLd(r)) }
        : {}),
    };

    items.push(localBusiness);
  }
  return items;
};

export const mapToSeoData = (
  data?: AboutModel | ReviewsTabModel | SalariesTabModel,
) => {
  if (!data) return undefined;

  const isSalariesTab = 'salaryRanges' in data;

  const name = isSalariesTab
    ? data.companyName
    : (data as AboutModel | ReviewsTabModel).name;

  const url = !isSalariesTab
    ? (data as AboutModel | ReviewsTabModel).overview?.website?.url
    : undefined;

  const logo = !isSalariesTab
    ? (data as AboutModel | ReviewsTabModel).branding?.logoImageUrl
    : undefined;

  const location = !isSalariesTab
    ? (data as AboutModel | ReviewsTabModel).overview?.primaryLocation
    : undefined;

  return {
    name,
    url,
    logo,
    location,
    ratingCount: data.reviews?.rating?.count ?? 0,
    ratingValue: data.reviews?.rating?.average ?? 0,
    ...(data.reviews?.reviews?.length ? { reviews: data.reviews.reviews } : {}),
    isSalariesTab,
  };
};
